import { useState, useContext, useEffect, useRef } from "react";
import { MyContext, ListContext } from "./MyContext";
import { currentDayToDB } from "./momentUtils";
import posthog from "posthog-js";
import { captureWentOffline } from "./networkTelemetry";
import { buildPaymentDetails } from "./paymentDetails";
import { getPaymentErrorMessage } from "./paymentErrorMessages";
import Auth from "./Auth";

export const useManualPayment = ({ onFinish, onError }) => {
  const { listState, dispatch2 } = useContext(ListContext);
  const { mystate, dispatch } = useContext(MyContext);
  const [processing, setProcessing] = useState(false);
  const submittingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      dispatch2(["CLEAR_CART"]);
      dispatch2(["SET_CLIENT_PAYMENT", {}]);
      dispatch2(["SET_INVOICE_PAYMENT", {}]);
    };
  }, [dispatch2]);

  const submitPayment = async (clientPayment) => {
    if (submittingRef.current || !mountedRef.current) return;
    submittingRef.current = true;
    setProcessing(true);
    dispatch({ type: "FETCH_INIT" });
    try {
      let result;
      try {
        const params = {
          ...buildPaymentDetails(listState),
          accountHolderPeopleId: listState.accountHolder.id,
          student_id: listState.quickpaystudentid || null,
          is_quickpay: listState.quickpaystudentid,
          person_id: mystate.currentStudent.person_id,
          total: listState.cartTotal,
          date: clientPayment.purchase_date || currentDayToDB(),
          transaction_type: clientPayment.transaction_type,
          payment_notes: clientPayment.payment_notes,
          reference: clientPayment.reference,
          online_purchase: 0,
        };
        const isCreatingInvoice = clientPayment.transaction_type === "invoice";
        const isInvoicePayment =
          listState.currentInvoicePayment?.is_invoice === "invoice";
        const hasInvoicePaymentId = Boolean(
          listState.currentInvoicePayment?.payment_id,
        );
        if (!isCreatingInvoice && isInvoicePayment && hasInvoicePaymentId) {
          params.is_invoice_payment =
            listState.currentInvoicePayment.is_invoice || "no";
          params.payment_id = listState.currentInvoicePayment.payment_id || 0;
        }
        result = await Auth.api(
          "payments/createPaymentFromStripe",
          "post",
          params,
          {
            skipGlobalErrorHandler: clientPayment.transaction_type === "saved",
          },
        );
      } catch (error) {
        if (!mountedRef.current) return;
        const isOffline = !navigator.onLine || error.isOfflineBlocked;
        if (isOffline) {
          captureWentOffline({
            source: "payment_creation",
            api_endpoint: "payments/createPaymentFromStripe",
            user_action: "submit_payment",
          });
        } else {
          posthog?.captureException(error, {
            api_endpoint: "payments/createPaymentFromStripe",
            context: "payment_creation",
            user_action: "submit_payment",
            status: error.response?.status,
          });
        }
        dispatch({ type: "FETCH_ERROR", error });
        onError(
          isOffline
            ? "You appear to be offline. Please reconnect and try again."
            : getPaymentErrorMessage(error, {
                staff: true,
                quickPay:
                  Boolean(listState.quickpaystudentid) &&
                  clientPayment.transaction_type === "saved",
                fallback:
                  "There was an error creating the payment. Please try again.",
              }),
        );
        return;
      }
      if (!mountedRef.current) return;
      const data = result?.data;
      if (
        listState.quickpaystudentid &&
        clientPayment.transaction_type === "saved" &&
        data?.status !== "fulfilled"
      ) {
        dispatch({ type: "FETCH_ERROR" });
        onError(
          "Quick Pay card payment has not been fulfilled. Check its status before submitting again.",
        );
        return;
      }
      const itemIds =
        listState.quickpaystudentid && data?.status === "fulfilled"
          ? data.item_ids
          : data?.returnArray;
      if (
        !data ||
        (listState.quickpaystudentid &&
          (!Array.isArray(itemIds) || !itemIds.length))
      ) {
        dispatch({ type: "FETCH_ERROR" });
        onError(
          "The server response is missing payment details. Check the payment before submitting again.",
        );
        return;
      }
      dispatch({ type: "FETCH_SUCCESS" });
      try {
        await onFinish(itemIds);
      } catch (error) {
        posthog?.captureException(error, { context: "payment_completion" });
        if (mountedRef.current)
          onError(
            "The payment completed, but the screen could not update. Refresh before submitting again.",
          );
      }
      return data;
    } finally {
      submittingRef.current = false;
      if (mountedRef.current) setProcessing(false);
    }
  };
  return { submitPayment, processing };
};
